import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { useAuthStore } from '@/stores/auth-store';
import { MessageSquare, Shield } from 'lucide-react';

interface Props {
  clubId: number;
  // (Optionnel) si tu l’as au niveau page club, passe-le pour un message support + clair
  clubName?: string;
}

/**
 * Revendication de club — GELÉE côté front (plan FFPJP auto-déclaration §10bis).
 *
 * La revendication automatique par claim/vote est temporairement indisponible :
 * le référentiel licence FFPJP n'étant plus fiable, on ne propose plus
 * d'auto-validation (ni par vote, ni par licence, ni par déclaration). Les
 * demandes passent par le support. La création/onboarding d'un club avec
 * certification juridique reste gérée côté backend (rôle CLUB_ADMIN).
 *
 * Seul l'affichage du badge « Administrateur du club » (claim déjà validé,
 * club actif) est conservé.
 */
export function ClaimAdminSection({ clubId, clubName }: Props) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const user = useAuthStore(s => s.user);

  const isClubAdminOfThisClub = isAuthenticated && user?.isClubAdmin && user?.clubId === clubId;

  // Admin déjà validé de ce club → badge informatif
  if (isClubAdminOfThisClub) {
    return (
      <div className='bg-white rounded-2xl border border-slate-100 shadow-sm p-6'>
        <div className='flex items-center gap-3'>
          <div className='w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center shrink-0'>
            <Shield className='w-5 h-5 text-green-600' />
          </div>
          <div>
            <h3 className='font-bold text-slate-900'>Administrateur du club</h3>
            <p className='text-xs text-slate-400'>Vous gérez ce club sur BoulesConnect</p>
          </div>
          <span className='ml-auto px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-bold'>
            Admin
          </span>
        </div>
      </div>
    );
  }

  // Parcours de revendication automatique gelé → message support neutre
  const subject = 'Gestion de mon club';
  const message = [
    'Bonjour,',
    '',
    'Je souhaite revendiquer la gestion de ce club sur BoulesConnect.',
    '',
    '--- Contexte club ---',
    `Club : ${clubName ?? '(nom inconnu)'}`,
    `Club ID : ${clubId}`,
    '',
    'Merci de me recontacter pour la procédure.',
    'Cordialement',
  ].join('\n');
  const supportTo = `${ROUTES.CONTACT}?subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}`;

  return (
    <div className='bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-3'>
      <div className='flex items-center gap-3'>
        <div className='w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0'>
          <Shield className='w-5 h-5 text-slate-500' />
        </div>
        <div>
          <h3 className='font-bold text-slate-900'>Revendication de club</h3>
          <p className='text-xs text-slate-400'>Temporairement indisponible</p>
        </div>
      </div>

      <p className='text-sm text-slate-600'>
        La revendication automatique de club est temporairement indisponible. Pour toute demande, contactez le support.
      </p>

      <p className='text-xs text-slate-400 flex items-center gap-1.5'>
        <MessageSquare className='w-3.5 h-3.5 shrink-0' />
        <Link to={supportTo} className='text-blue-600 hover:underline font-medium'>
          Contacter le support
        </Link>
      </p>
    </div>
  );
}
